import { Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useCart } from "@/lib/cart";
import { aud } from "@/lib/catalog";
import { Stepper } from "./Reveal";
import { StateSelect } from "./StateSelect";

export const CartDrawer = () => {
  const c = useCart();
  const goCheckout = () => {
    c.setOpen(false);
    c.setCheckout(true);
  };

  return (
    <Sheet open={c.open} onOpenChange={c.setOpen}>
      <SheetContent data-testid="cart-drawer-container" data-lenis-prevent className="flex w-full flex-col border-l-0 bg-[#F5F0E8] p-0 sm:max-w-md">
        <SheetHeader className="border-b border-[#112E23]/15 px-6 py-6 text-left">
          <SheetTitle className="font-display text-4xl font-black uppercase text-[#112E23]">Your cart</SheetTitle>
          <SheetDescription className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#6E472B]">{c.count} ball{c.count === 1 ? "" : "s"}</SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {c.lines.length === 0 && <p data-testid="cart-empty-message" className="py-16 text-center text-[#112E23]/75">Your cart is empty. Pick a ball to get started.</p>}
          {c.lines.map((l) => (
            <div key={l.key} data-testid={`cart-line-${l.key}`} className="flex gap-4 border-b border-[#112E23]/10 py-4">
              <img src={l.image} alt={l.full} className="h-20 w-20 shrink-0 rounded-2xl object-cover" />
              <div className="flex flex-1 flex-col gap-2">
                <div className="flex justify-between gap-2">
                  <div>
                    <div className="font-display text-xl font-extrabold uppercase text-[#112E23]">{l.name}</div>
                    {l.variant && <div className="font-mono text-[11px] uppercase tracking-widest text-[#6E472B]">{l.variant}</div>}
                    <div className="font-mono text-xs text-[#112E23]/70">{aud(l.price)} each</div>
                  </div>
                  <div className="font-mono text-sm font-semibold text-[#112E23]">{aud(l.total)}</div>
                </div>
                <div className="flex items-center justify-between">
                  <Stepper id={`cart-item-${l.key}`} value={l.qty} onChange={(v) => c.setQty(l.key, v)} />
                  <button data-testid={`cart-item-${l.key}-remove-button`} aria-label="Remove" onClick={() => c.setQty(l.key, 0)} className="rounded-full p-2 text-[#6E472B] transition-colors hover:bg-[#112E23]/5">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-4 border-t border-[#112E23]/15 bg-white/60 px-6 py-6">
          <label className="block">
            <span className="mb-1.5 block text-sm font-semibold text-[#112E23]">Delivery postcode</span>
            <Input
              data-testid="cart-postcode-input"
              inputMode="numeric"
              maxLength={4}
              placeholder="e.g. 6023"
              value={c.postcode}
              onChange={(e) => c.setPostcode(e.target.value.replace(/\D/g, ""))}
              className="h-12 rounded-xl border-[#112E23]/20 bg-white"
            />
          </label>
          <StateSelect value={c.state} onChange={c.setState} testId="cart-state-select-dropdown" className="border-[#112E23]/20 bg-white" />
          <div className="space-y-1.5 font-mono text-sm text-[#112E23]">
            <div className="flex justify-between"><span>Subtotal</span><span data-testid="cart-subtotal">{aud(c.subtotal)}</span></div>
            <div className="flex justify-between"><span>Postage{c.state ? ` (${c.state})` : ""}</span><span data-testid="cart-postage">{c.state || c.postcode ? aud(c.postage) : "Choose state"}</span></div>
            <div className="flex justify-between pt-2 text-lg font-semibold"><span>Total AUD</span><span data-testid="cart-total">{aud(c.total)}</span></div>
          </div>
          <button data-testid="cart-checkout-button" disabled={!c.count} onClick={goCheckout} className="btn-dark w-full justify-center disabled:opacity-40">
            Checkout
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
